<template>
  <Modal
    :open="props.open"
    @close-modal="$emit('close-modal')"
    customHeight="lg:h-128"
    customWidth="lg:w-120"
  >
    <template #modal-header>
      <div>
        <div class="flex mb-2 items-center">
          <Icon name="BadgeDollarSign" class="w-4 h-4 mr-2 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Phoenix Flex</div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          Phoenix Flex Notice Window
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-6">
        <div
          v-if="props.investment.opt_out_decision_complete"
          class="rounded-xl bg-bg-canvas mb-4 text-sm font-inter text-strong"
        >
          <div v-if="props.investment.opt_out_auto_extend">
            You selected to redeem your Phoenix Flex Notes.
          </div>
          <div v-else>You selected to continue your Phoenix Flex Notes.</div>
        </div>
        <div class="mb-3 text-strongest">
          You can update your selection anytime while the redemption window is open. Continuing
          keeps your notes earning interest through your next Set Put Date on
          {{
            props.investment.next_put_date
              ? new Date(props.investment.next_put_date)?.toLocaleDateString("en-US", {
                  month: "numeric",
                  day: "numeric",
                  year: "numeric",
                })
              : "[Not Available]"
          }}. Choosing to redeem means your notes will be processed for redemption.
        </div>
        <div>
          <BaseInput
            v-model="decision"
            label="Continue Investment?"
            field_type="dropdown"
            variant="secondary"
            :dropdown_options="['Yes, continue investment', 'No, exit investment']"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex justify-end gap-2">
        <Button @click="$emit('close-modal')" variant="paper-primary" size="sm" class="">
          <template #label>Cancel</template>
        </Button>
        <Button @click="handleConfirm()" variant="primary-grey" size="sm" class="">
          <template #label>Confirm</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
// IMPORTS
import { computed, ref, watch } from "vue";
import { Modal, TabView, Form, Icon, Button, BankCard, BaseInput } from "@/components";
import { useCrudStore, useSchemaStore, useUIStore, useDataStore } from "@/stores";
import _ from "lodash";

//STORES
const crudStore = useCrudStore();
const schemaStore = useSchemaStore();
const dataStore = useDataStore();
const UIStore = useUIStore();

// EMITS & PROPS
const emit = defineEmits(["close-modal", "refresh"]);
const props = defineProps({
  open: { type: Boolean, required: true },
  investment: { type: Object, required: true },
});
const decision = ref("Yes, continue investment");

// COMPONENT LOGIC AND FUNCTIONS
async function handleConfirm() {
  try {
    let query = [
      {
        field_name: "record_id",
        operator: "eq",
        value: props.investment.record_id,
      },
    ];

    let update = {
      opt_out_decision_complete: true,
      opt_out_auto_extend: decision.value === "No, exit investment" ? true : false,
    };
    update = schemaStore.addCreatedUpdatedFields(update, false, true);
    await crudStore.update("investments", query, update);

    let update_res = await crudStore.update("investments", query, update);

    if (update_res.status !== 200) {
      throw new Error("Failed to update investment opt-out decision");
    }

    let message =
      decision.value === "No, exit investment"
        ? "You have opted out of the auto-renewal. Your investment will mature on the next available put date."
        : "You have opted to continue your investment. Your funds will remain invested and continue to earn interest.";

    UIStore.animateNotificationAlert({
      type: "Success",
      icon: "Check",
      message: message,
    });

    // find the investment in the dataStore and update it
    const inv = dataStore.investments?.find((i) => i.record_id === props.investment.record_id);
    if (inv) {
      inv.opt_out_decision_complete = true;
      inv.opt_out_auto_extend = decision.value === "No, exit investment" ? true : false;
    }

    emit("close-modal");
    emit("refresh");
  } catch (e) {
    UIStore.animateNotificationAlert({
      type: "Error",
      icon: "X",
      message: "Failed to update investment opt-out decision. Please try again.",
    });
  }
}
</script>
